<script setup>
import { defineProps, useSlots, ref, computed, watch } from 'vue'
import { injectDocExpansion } from '@/composables/useDocExpansion'

const props = defineProps({
  name: String,
  type: String,
  description: String,
  depth: { type: Number, default: 0 }
})

const slots = useSlots()
const hasChildren = computed(() => slots.default && slots.default().length > 0)
const isExpanded = ref(props.depth === 0)

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

// Listen for master expand/collapse events
const docExpansion = injectDocExpansion()
if (docExpansion) {
  watch(() => docExpansion.expandAll.value, (newValue) => {
    if (newValue && hasChildren.value) {
      isExpanded.value = newValue.state
    }
  })
}
</script>

<template>
  <div class="attribute-wrapper">
    <div class="attribute-header"
         :class="{ 'clickable': hasChildren }"
         @click="hasChildren && toggleExpanded()">
      <div class="d-flex justify-content-between align-items-start">
        <div class="flex-grow-1">
          <div class="d-flex align-items-center mb-2">
            <span class="me-2 attribute-name">{{ props.name }}</span>
            <span class="type-badge">{{ props.type }}</span>
          </div>
          <p class="attribute-description">{{ props.description }}</p>
        </div>
        <font-awesome-icon v-if="hasChildren"
                           :icon="['fas', isExpanded ? 'caret-down' : 'caret-right']"
                           class="caret-icon" />
      </div>
    </div>
    <Transition name="expand">
      <div v-if="!hasChildren || isExpanded" class="attribute-children">
        <slot></slot>
      </div>
    </Transition>
  </div>
</template>

<style scoped>

.form-control {
  padding: 1rem;
  border-radius: var(--radius-md);
  border:1px solid var(--border);
}

label {
  padding:1rem;
  font-weight: normal;
}

.form-floating > .form-control:focus, .form-floating > .form-control:not(:placeholder-shown) {
  padding-top: 1.2rem;
  padding-bottom: 0;
}

.attribute-wrapper {
  margin-bottom: 0.75rem;
}

.attribute-header {
  padding: 0.75rem 1rem;
  border-radius: var(--radius-lg);
  background-color: var(--surface-sunken);
  transition: var(--transition);
}

.attribute-header.clickable {
  cursor: pointer;
}

.attribute-header.clickable:hover {
  background-color: var(--surface-inset);
}

.attribute-name {
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  font-family: var(--font-heading);
}

.type-badge {
  font-size: var(--fs-xs);
  font-weight: 500;
  color: var(--accent-text);
  background: var(--accent-soft-2);
  padding: 0.1rem 0.5rem;
  border-radius: var(--radius-xs);
}

.attribute-description {
  color: var(--text-muted);
  font-size: 0.85rem;
  margin: 0;
  line-height: var(--lh-normal);
}

.attribute-children {
  padding-left: 1.5rem;
  margin-top: 0.75rem;
}

.caret-icon {
  color: var(--text-secondary);
  transition: var(--transition-slow);
  font-size: 1.3rem;
  margin-left: 1rem;
  margin-top: 0.25rem;
  flex-shrink: 0;
}

.attribute-header.clickable:hover .caret-icon {
  color: var(--text-muted);
}

.expand-enter-active, .expand-leave-active {
  transition: var(--transition-slow);
  overflow: hidden;
}

.expand-enter-from, .expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to, .expand-leave-from {
  opacity: 1;
  max-height: 2000px;
}

</style>
